import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Flex,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { useAutoSaveContext } from "newui/components/ui/AutoSave";
import { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import dayjs from "newui/utils/dayjs";
import { RfxDocumentRow } from "../types/rfx-document.types";
import { InlineEditableCell } from "./InlineEditableCell";

interface DocumentMobileCardProps {
  categoryOptions: SelectOption[];
  doc: RfxDocumentRow;
  /** Hide the Revision row (parity with table `hideRevision`). */
  hideRevision?: boolean;
  isSelected: boolean;
  onDeleteRequest: (doc: RfxDocumentRow) => void;
  onToggleSelect: (id: number) => void;
  onUpdate: (
    docId: number,
    data: { category_id?: number; external_revision?: string }
  ) => Promise<void>;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Stacked single-document card for narrow viewports. Renders the same data
 * fields as `DocumentTableRow` but in a label/value layout that doesn't
 * require horizontal scrolling. Selection, filtering, inline category /
 * revision edits, download and delete are all preserved. Drag-reorder is
 * intentionally dropped — long-press DnD on touch is fiddly and reorder is
 * a power-user operation better done on desktop.
 */
export const DocumentMobileCard = ({
  doc,
  isSelected,
  categoryOptions,
  hideRevision = false,
  onToggleSelect,
  onUpdate,
  onDeleteRequest,
}: DocumentMobileCardProps) => {
  const autoSave = useAutoSaveContext();
  const [savingField, setSavingField] = useState<"category" | "revision" | null>(null);

  const handleSaveCategory = async (value: string | number) => {
    setSavingField("category");
    autoSave?.setSaving(true);
    try {
      await onUpdate(doc.id, { category_id: Number(value) });
      autoSave?.setLastSavedAt(new Date());
    } catch {
      autoSave?.setError("Failed to save category");
    } finally {
      setSavingField(null);
      autoSave?.setSaving(false);
    }
  };

  const handleSaveRevision = async (value: string | number) => {
    setSavingField("revision");
    autoSave?.setSaving(true);
    try {
      await onUpdate(doc.id, { external_revision: String(value) });
      autoSave?.setLastSavedAt(new Date());
    } catch {
      autoSave?.setError("Failed to save revision");
    } finally {
      setSavingField(null);
      autoSave?.setSaving(false);
    }
  };

  const Row = ({
    label,
    children,
  }: {
    label: string;
    children: React.ReactNode;
  }) => (
    <Flex justify="space-between" align="center" gap={3} minH="32px">
      <Text fontSize="xs" color="gray.500" flexShrink={0}>
        {label}
      </Text>
      <Box flex={1} minW={0} textAlign="right">
        {children}
      </Box>
    </Flex>
  );

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor={isSelected ? "primary.300" : "gray.200"}
      bg={isSelected ? "primary.50" : "white"}
      p={3}
      data-testid={`document-mobile-card-${doc.id}`}
    >
      {/* Header: checkbox + filename + action buttons.
          Filename uses overflow-wrap:anywhere so single-token names break. */}
      <Flex align="flex-start" gap={2} mb={2}>
        <Checkbox
          isChecked={isSelected}
          onChange={() => onToggleSelect(doc.id)}
          data-testid={`document-checkbox-${doc.id}`}
          mt={1}
        />
        <Text
          flex={1}
          fontSize="sm"
          fontWeight="medium"
          wordBreak="break-word"
          sx={{ overflowWrap: "anywhere" }}
        >
          {doc.fileName}
        </Text>
        <Flex gap={1}>
          <IconButton
            aria-label="Download document"
            icon={<FiDownload />}
            size="sm"
            variant="ghost"
            as="a"
            href={doc.documentPath}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`doc-download-${doc.id}`}
          />
          <IconButton
            aria-label="Delete document"
            icon={<FiTrash2 />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => onDeleteRequest(doc)}
            data-testid={`doc-delete-${doc.id}`}
          />
        </Flex>
      </Flex>

      <Stack spacing={1} divider={<Box borderTopWidth="1px" borderColor="gray.100" />}>
        <Row label="Size">
          <Text fontSize="sm">{formatFileSize(doc.fileSize)}</Text>
        </Row>
        <Row label="Category">
          <InlineEditableCell
            mode="select"
            value={doc.categoryId}
            options={categoryOptions}
            onSave={handleSaveCategory}
            isSaving={savingField === "category"}
            testId={`doc-category-${doc.id}`}
          />
        </Row>
        {/* External revision is user-facing and always editable; the
            internal integer revision is hidden and bumped server-side. */}
        <Row label="Revision">
          <InlineEditableCell
            mode="text"
            value={
              doc.externalRevision && doc.externalRevision !== "0"
                ? doc.externalRevision
                : String(doc.internalRevision ?? 0)
            }
            onSave={handleSaveRevision}
            isSaving={savingField === "revision"}
            testId={`doc-revision-${doc.id}`}
          />
        </Row>
        <Row label="Uploaded">
          <Text fontSize="xs" color="gray.600">
            {dayjs(doc.createdAt).format("DD MMM YYYY")}
          </Text>
        </Row>
        <Row label="Uploaded by">
          <Text fontSize="xs" color="gray.600">
            {doc.uploadedBy}
          </Text>
        </Row>
      </Stack>
    </Box>
  );
};
